import {
  Box,
  Radio,
  Stack,
  useTheme,
  Accordion,
  Typography,
  FormControlLabel,
  AccordionDetails,
} from "@mui/material";
import type { SyntheticEvent } from "react";

import { PaymentPlanItem } from "./PaymentPlanItem";
import { PaymentPlansTable } from "./PaymentPlansTable";

import type { PaymentPlansType } from "@/types";

interface PaymentPlansProps {
  paymentPlans: PaymentPlansType[];
  onChange: (event: SyntheticEvent<Element, Event>) => void;
}

export function PaymentPlans({ onChange, paymentPlans }: PaymentPlansProps) {
  const { breakpoints } = useTheme();
  const fontSize = breakpoints.down(490) ? "0.875rem" : "1rem";
  return (
    <Stack>
      {paymentPlans.length > 0 ? (
        <Stack
          sx={{
            mt: 2,
          }}
        >
          <Typography fontSize={"20px"} fontWeight={700}>
            Payment Options
          </Typography>
          <Stack gap={3} mt={1}>
            {paymentPlans.map((paymentPlan, idx) => (
              <Stack key={paymentPlan.id}>
                <Accordion
                  expanded
                  sx={{
                    boxShadow: "none",
                    borderWidth: "0.1px",
                    borderStyle: "solid",
                    borderColor: "rgba(0, 0, 0, 0.23)",
                  }}
                >
                  <FormControlLabel
                    onChange={onChange}
                    control={<Radio size="small" />}
                    value={paymentPlan.id}
                    checked={!!paymentPlan.selected}
                    label={<PaymentPlanItem paymentPlan={paymentPlan} />}
                    sx={{
                      "&.MuiFormControlLabel-root": {
                        mx: 0,
                        width: "100%",
                      },
                      "& .MuiFormControlLabel-label": {
                        ml: 0,
                        width: "100%",
                      },
                      "& .MuiButtonBase-root.MuiAccordionSummary-root": {
                        p: "0 !important",
                        minHeight: "42px",
                        maxHeight: "42px",
                      },
                    }}
                  />
                  <AccordionDetails
                    sx={{
                      "&.MuiAccordionDetails-root": {
                        display: "block",
                        pt: 0,
                        pb: 1,
                      },
                    }}
                  >
                    {paymentPlan?.description ? (
                      <Typography
                        variant="caption"
                        mx={2}
                        sx={{ display: "block" }}
                      >
                        {paymentPlan.description}
                      </Typography>
                    ) : null}

                    {idx != 0 ? (
                      <PaymentPlansTable
                        applyTopMargin={!!paymentPlan?.description}
                        installments={paymentPlan.installments}
                      />
                    ) : null}

                    <Stack
                      ml={0.5}
                      mt={1}
                      gap={0.3}
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Typography
                        fontSize={fontSize}
                        fontWeight={400}
                        color="gray"
                      >
                        Payment Total
                      </Typography>
                      <Typography
                        color="text.primary"
                        fontWeight={600}
                        fontSize={fontSize}
                      >
                        ${paymentPlan.totalAmount?.toFixed(2)}
                      </Typography>
                      {!paymentPlan?.title ? <Box flexGrow={1} /> : null}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              </Stack>
            ))}
          </Stack>
        </Stack>
      ) : null}
    </Stack>
  );
}
